import { useState } from 'react';
import { generateShareContent } from './generateCaseReport';

export const useShareReport = (data, caseResolutions, hasProfileData, profileData) => {
  // Share Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [shareSections, setShareSections] = useState({
    clientProfile: true,
    clientSummary: true,
    assessment: true,
    blockers: true,
    risks: true,
    pathways: true,
    missingInfo: true,
    nextSteps: true,
    actionItems: true,
    professionalAdvice: true,
    clientCommunication: true,
    longTermOutlook: true,
    firmCompliance: true,
    internalNotes: false
  });

  // Toggle section for sharing
  const toggleShareSection = (section) => {
    setShareSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Select/Deselect all sections
  const selectAllSections = (selectAll) => {
    setShareSections(Object.keys(shareSections).reduce((acc, key) => {
      acc[key] = selectAll;
      return acc;
    }, {}));
  };

  // Share via email
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Case Analysis Report - ${data?.summary?.clientName || 'Client'}`);
    const body = encodeURIComponent(generateShareContent('text', data, shareSections, hasProfileData, profileData));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowShareModal(false);
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateShareContent('text', data, shareSections, hasProfileData, profileData));
      setShowCopiedToast(true);
      setTimeout(() => setShowCopiedToast(false), 3000);
      setShowShareModal(false);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download as HTML
  const downloadAsHTML = () => {
    const content = generateShareContent('html', data, shareSections, hasProfileData, profileData);
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `case-report-${data?.summary?.clientName || 'client'}-${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowShareModal(false);
  };

  // Native share (for mobile/Windows share)
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Case Analysis - ${data?.summary?.clientName || 'Client'}`,
          text: generateShareContent('text', data, shareSections, hasProfileData, profileData),
        });
        setShowShareModal(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  // Print / Save as PDF
  const printReport = () => {
    const content = generateShareContent('html', data, shareSections, hasProfileData, profileData);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    setShowShareModal(false);
  };

  return {
    showShareModal,
    setShowShareModal,
    showCopiedToast,
    shareSections,
    toggleShareSection,
    selectAllSections,
    shareViaEmail,
    copyToClipboard,
    downloadAsHTML,
    nativeShare,
    printReport,
  };
};
